import { Link } from 'react-router-dom';
import { useDownloadOnlyTaskUsers } from '../features/download-only-task-users';
import type { DownloadOnlyTaskUserDTO } from '../features/download-only-task-users';
import { formatDateTime } from '../lib/format';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

function userLabel(item: DownloadOnlyTaskUserDTO): string {
  return item.email || item.nickname || `用户 ${item.user_id}`;
}

export function DownloadOnlyTaskUsersPage() {
  const {
    items,
    total,
    page,
    pageSize,
    totalPages,
    userId,
    keyword,
    accountKeyword,
    nameKeyword,
    targetHash,
    loading,
    error,
    refreshing,
    updatingUserIds,
    setFilter,
    setPage,
    setPageSize,
    resetFilters,
    reload,
    refreshList,
    updateShowNormalTasks,
  } = useDownloadOnlyTaskUsers();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-700">
          用户 ID
          <input
            value={userId}
            onChange={(event) => setFilter('userId', event.target.value)}
            className="ml-2 h-9 w-32 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <label className="text-sm text-gray-700">
          用户关键字
          <input
            value={keyword}
            onChange={(event) => setFilter('keyword', event.target.value)}
            placeholder="邮箱/昵称"
            className="ml-2 h-9 w-44 rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <label className="text-sm text-gray-700">
          提现账号
          <input
            value={accountKeyword}
            onChange={(event) => setFilter('accountKeyword', event.target.value)}
            className="ml-2 h-9 w-44 rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <label className="text-sm text-gray-700">
          支付宝姓名
          <input
            value={nameKeyword}
            onChange={(event) => setFilter('nameKeyword', event.target.value)}
            className="ml-2 h-9 w-40 rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <label className="text-sm text-gray-700">
          Target Hash
          <input
            value={targetHash}
            onChange={(event) => setFilter('targetHash', event.target.value)}
            className="ml-2 h-9 w-56 rounded-md border border-gray-300 px-3 text-sm font-mono focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <button
          onClick={resetFilters}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
        >
          重置
        </button>
        <button
          onClick={reload}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
        >
          刷新
        </button>
        <button
          onClick={refreshList}
          disabled={refreshing}
          className="h-9 rounded-md bg-primary px-3 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60"
        >
          {refreshing ? '刷新中' : '刷新名单'}
        </button>
      </div>

      <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium">用户</th>
              <th className="px-4 py-3 text-left font-medium">提现账号</th>
              <th className="px-4 py-3 text-left font-medium">支付宝姓名</th>
              <th className="px-4 py-3 text-left font-medium">展示普通任务</th>
              <th className="px-4 py-3 text-left font-medium">提现账号 ID</th>
              <th className="px-4 py-3 text-left font-medium">Target Hash</th>
              <th className="px-4 py-3 text-left font-medium">来源</th>
              <th className="px-4 py-3 text-left font-medium">更新时间</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link to={`/users/${item.user_id}`} className="font-medium text-primary hover:underline">
                    {userLabel(item)}
                  </Link>
                  <div className="mt-0.5 text-xs tabular-nums text-gray-400">ID {item.user_id}</div>
                </td>
                <td className="px-4 py-3 text-gray-900">{item.target_masked || '-'}</td>
                <td className="px-4 py-3 text-gray-700">{item.account_name || '-'}</td>
                <td className="px-4 py-3">
                  <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                    <input
                      type="radio"
                      name={`normal-task-${item.target_hash}`}
                      checked={Boolean(item.show_normal_tasks)}
                      disabled={updatingUserIds.includes(item.user_id)}
                      onChange={(event) => {
                        if (event.target.checked) void updateShowNormalTasks(item.user_id, true);
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    {item.show_normal_tasks ? '当前展示' : '设为展示'}
                  </label>
                </td>
                <td className="px-4 py-3 tabular-nums text-gray-600">{item.withdrawal_account_id}</td>
                <td className="max-w-xs truncate px-4 py-3 font-mono text-xs text-gray-500">{item.target_hash || '-'}</td>
                <td className="px-4 py-3 text-gray-600">{item.source}</td>
                <td className="px-4 py-3 text-gray-600">{item.updated_at ? formatDateTime(item.updated_at) : '-'}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                  暂无下载任务限制名单
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
        <span>共 {total} 条</span>
        <div className="flex items-center gap-2">
          <select
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
            className="h-8 rounded-md border border-gray-300 px-2 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((value) => (
              <option key={value} value={value}>{value} / 页</option>
            ))}
          </select>
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="h-8 rounded-md border border-gray-300 px-3 disabled:opacity-50"
          >
            上一页
          </button>
          <span className="tabular-nums">{page} / {totalPages}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="h-8 rounded-md border border-gray-300 px-3 disabled:opacity-50"
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  );
}
